import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronRight, Zap, RefreshCw, BarChart2, Clock, ArrowRight, X as CloseIcon, Settings, FileCheck } from 'lucide-react';
import { sendContactForm } from '../services/emailService';

const ProcessAutomationPage = () => {
  // States for form and modal
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' }); // Track submission status
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    inquiryType: 'get-started' // Default inquiry type
  });

  // Navigation to home page
  const navigateToHome = () => {
    window.location.href = '/';
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: '',
      inquiryType: 'get-started'
    });
  };

  // Open modal with specific inquiry type
  const openModal = (type) => {
    setFormData(prev => ({...prev, inquiryType: type}));
    setShowModal(true);
    // Reset status when opening modal
    setSubmitStatus({ success: null, message: '' });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Send form data via EmailJS
    sendContactForm(formData)
      .then((response) => {
        console.log('Email sent successfully!', response);
        setSubmitStatus({
          success: true,
          message: 'Thanks for your interest! We will contact you soon.'
        });
        // Clear form but don't close modal immediately to show success message
        resetForm();
        // Close modal after delay to show success message
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus({ success: null, message: '' });
        }, 3000);
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setSubmitStatus({
          success: false,
          message: 'We could not send this automatically. Please email us at jacobvinoo@gmail.com with the details above.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const bottleneckData = [
    {
      process: 'Quote follow-up',
      manualHours: 9.5,
      waitingHours: 14,
      automationFit: 92,
      firstMove: 'Auto-reminders and next-step prompts after every quote',
    },
    {
      process: 'Scheduling',
      manualHours: 7.2,
      waitingHours: 10.5,
      automationFit: 84,
      firstMove: 'Route jobs by team availability, suburb, and priority',
    },
    {
      process: 'Invoice chasing',
      manualHours: 6.1,
      waitingHours: 8.8,
      automationFit: 95,
      firstMove: 'Send polite payment nudges before cash flow slips',
    },
    {
      process: 'Stock updates',
      manualHours: 5.8,
      waitingHours: 5.4,
      automationFit: 78,
      firstMove: 'Flag low-stock items from job sheets and supplier lead times',
    },
    {
      process: 'Handover',
      manualHours: 4.4,
      waitingHours: 7.2,
      automationFit: 81,
      firstMove: 'Create summaries, photos, warranties, and follow-up tasks',
    },
  ];

  const bottleneckSummary = [
    { label: 'Biggest bottleneck', value: 'Quote follow-up', detail: '23.5 hours of weekly drag', color: 'text-pink-200' },
    { label: 'Fastest quick win', value: 'Invoice chasing', detail: 'high-fit reminders and escalation', color: 'text-emerald-200' },
    { label: 'Hidden delay', value: 'Job scheduling', detail: 'handoffs stall the next job', color: 'text-cyan-200' },
  ];

  const BottleneckTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const row = payload[0]?.payload;

    return (
      <div className="max-w-xs rounded-lg border border-gray-700 bg-gray-950/95 p-3 shadow-xl">
        <p className="mb-2 font-semibold text-white">{label}</p>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-6 text-sm">
            <span className="text-gray-300">{entry.name}</span>
            <span className="font-semibold" style={{ color: entry.color }}>
              {entry.value}h/wk
            </span>
          </div>
        ))}
        <div className="mt-3 border-t border-gray-800 pt-3 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-400">Automation fit</span>
            <span className="font-semibold text-emerald-300">{row.automationFit}%</span>
          </div>
          <p className="mt-2 text-gray-300">{row.firstMove}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-6 py-4 lg:px-12 bg-gray-900">
        <div className="flex items-center cursor-pointer" onClick={navigateToHome}>
          <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">Mosyni AI</span>
        </div>
        <div className="hidden md:flex space-x-8">
          <a href="/#services" className="hover:text-blue-400 transition">Services</a>
          <a href="/#benefits" className="hover:text-blue-400 transition">Benefits</a>
          <a href="/#methodology" className="hover:text-blue-400 transition">Methodology</a>
          <a href="/#contact" className="hover:text-blue-400 transition">Contact</a>
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-full font-semibold transition" onClick={navigateToHome}>
          Back to Home
        </button>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-900 to-gray-900">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
              Process Automation Solutions
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Streamline operations and eliminate repetitive tasks with AI-powered automation. Our process automation solutions help small businesses boost efficiency, reduce errors, and free up valuable time for growth-focused activities.
            </p>
            <button
              onClick={() => openModal('get-started')}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-full font-semibold text-lg transition flex items-center"
            >
              Get Started
              <ChevronRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">How Our Automation Solutions Transform Your Business</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-blue-600 rounded-lg inline-block mb-4">
              <RefreshCw size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-gray-300">
              Streamline complex business processes with intelligent workflows that automatically route tasks, documents, and approvals without manual intervention.
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-purple-600 rounded-lg inline-block mb-4">
              <FileCheck size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Document Processing</h3>
            <p className="text-gray-300">
              Extract, categorize, and process information from documents using AI that learns to understand your specific business forms and paperwork.
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-pink-600 rounded-lg inline-block mb-4">
              <Settings size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Integration Platform</h3>
            <p className="text-gray-300">
              Connect all your business applications with no-code integrations that enable seamless data flow between systems, eliminating data silos.
            </p>
          </div>
        </div>
      </div>

      {/* Sample Visualization */}
      <div className="bg-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Find the Bottlenecks Before Automating</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See where work slows down, which steps need too much manual effort, and which bottlenecks are good candidates for automation.
            </p>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-950 shadow-2xl shadow-black/30">
            <div className="grid grid-cols-1 border-b border-gray-800 md:grid-cols-3">
              {bottleneckSummary.map((item) => (
                <div key={item.label} className="border-b border-gray-800 p-5 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{item.label}</p>
                  <p className={`mt-2 text-2xl font-bold ${item.color}`}>{item.value}</p>
                  <p className="mt-1 text-sm text-gray-400">{item.detail}</p>
                </div>
              ))}
            </div>
            <div className="p-4 md:p-8">
              <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-xl font-bold">Workflow Bottleneck Scan</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    Waiting time often hurts more than typing time. This view shows both, so the first improvement is easier to choose.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-gray-300">
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-amber-400"></span>Manual touch time</span>
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-rose-500"></span>Waiting / handoff delay</span>
                </div>
              </div>
              <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bottleneckData} layout="vertical" margin={{ top: 12, right: 24, left: 20, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 12 }} tickLine={false} axisLine={{ stroke: '#475569' }} tickFormatter={(value) => `${value}h`} label={{ value: 'Weekly process drag', position: 'insideBottom', offset: -4, fill: '#cbd5e1', fontSize: 12 }} />
                  <YAxis type="category" dataKey="process" width={118} stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<BottleneckTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
                  <Bar dataKey="manualHours" stackId="drag" fill="#f59e0b" name="Manual touch time" radius={[6, 0, 0, 6]} />
                  <Bar dataKey="waitingHours" stackId="drag" fill="#f43f5e" name="Waiting / handoff delay" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-5">
                {bottleneckData.map((item) => (
                  <div key={item.process} className="rounded-lg border border-gray-800 bg-gray-900/70 p-3">
                    <p className="text-xs font-semibold text-gray-400">{item.process}</p>
                    <p className="mt-2 text-lg font-bold text-emerald-300">{item.automationFit}%</p>
                    <p className="text-xs text-gray-500">automation fit</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-4">
                <Zap className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <p className="text-sm text-cyan-50">
                  The best first automation is usually not the biggest task. It is the repeatable bottleneck with clear rules, visible delay, and low decision risk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Benefits of Process Automation</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transform how your business operates with intelligent automation that delivers measurable results.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <Clock size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Time Savings</h3>
              <p className="text-gray-300">
                Reduce time spent on routine tasks by 70-90%, freeing up your team to focus on creative, strategic work that drives business growth.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <BarChart2 size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Increased Accuracy</h3>
              <p className="text-gray-300">
                Eliminate up to 95% of human errors in data entry, calculations, and routine processing, improving quality and reducing costly mistakes.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <Zap size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Scalable Operations</h3>
              <p className="text-gray-300">
                Handle growth without proportional increases in staff by automating core processes that can scale to handle 5-10x your current workload.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <RefreshCw size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Consistent Service</h3>
              <p className="text-gray-300">
                Deliver reliable, 24/7 service with automated processes that never sleep, ensuring consistent customer experiences regardless of volume.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Automate Your Business Processes?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Our automation experts will help you identify the best opportunities for automation in your business and implement solutions that deliver rapid ROI.
          </p>
          <button
            onClick={() => openModal('consultation')}
            className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold text-lg transition flex items-center mx-auto"
          >
            Schedule a Free Consultation
            <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">What processes can be automated?</h3>
            <p className="text-gray-300">
              Almost any repetitive, rule-based process can be automated. Common examples include data entry, document processing, invoice management, customer onboarding, inventory updates, report generation, email responses, and approval workflows. We'll help you identify the processes that will deliver the highest ROI when automated.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">How long does it take to implement automation?</h3>
            <p className="text-gray-300">
              Simple automation workflows can be implemented in as little as 2-3 weeks. More complex, integrated automation systems typically take 4-8 weeks. We follow an agile approach, delivering value incrementally so you can see results quickly while we build out the complete solution.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">Do we need to replace our existing systems?</h3>
            <p className="text-gray-300">
              No. Our automation solutions are designed to work with your existing systems and software. We use API connections and modern integration techniques to automate processes across different platforms without requiring you to replace systems that are working well for your business.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">How much technical knowledge do we need?</h3>
            <p className="text-gray-300">
              Very little. Our solutions are designed with user-friendly interfaces that require minimal technical expertise to operate. We provide comprehensive training and documentation, and our no-code/low-code approach means your team can make simple changes without developer assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-md relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              disabled={isSubmitting}
            >
              <CloseIcon size={24} />
            </button>

            <div className="p-6">
              <h3 className="text-2xl font-bold mb-6 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
                Get Started with Mosyni AI
                {formData.inquiryType === 'consultation' && " - Consultation Request"}
              </h3>

              {submitStatus.success === null ? (
                <form onSubmit={handleSubmit}>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website || ''}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-1">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-1">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-1">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-1">How can we help you?</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.inquiryType === 'consultation' ?
                          formData.message || "I'm interested in scheduling a consultation about Process Automation solutions." :
                          formData.message}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      ></textarea>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-md transition-all duration-200"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Submit'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className={`p-4 rounded-md mb-4 ${submitStatus.success ? 'bg-green-700 bg-opacity-30' : 'bg-red-700 bg-opacity-30'}`}>
                  <p className="text-center">
                    {submitStatus.message}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessAutomationPage;
